import { useContext } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';

import { WebPanelLocationQuery } from './WebPanelLocationQuery.graphql';
import type {
	WebPanelLocationQuery as WebPanelLocationQueryType,
	WebPanelLocationQueryVariables as WebPanelLocationQueryVariablesType,
} from './__types__/WebPanelLocationQuery';
import type { WebPanel } from './WebPanelLocation';

export const useWebPanelLocation = ({
	spaceKey,
	contentId,
	location,
	// Query is preloaded on the server-side. reused the preloaded data.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	fetchPolicy = process.env.REACT_SSR ? 'cache-first' : 'cache-and-network',
	onCompleted,
}: {
	spaceKey?: string;
	contentId?: string;
	location: string;
	fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only';
	onCompleted?: () => void;
}) => {
	const { getQueryParams } = useContext(RoutesContext);
	const queryParams = getQueryParams();

	const spaceIdentifier =
		spaceKey || getSingleParam(queryParams, 'spaceKey') || getSingleParam(queryParams, 'key');
	const contentIdentifier = contentId || getSingleParam(queryParams, 'pageId');

	const variables = contentIdentifier
		? { contentId: contentIdentifier, location }
		: { spaceKey: spaceIdentifier, location };

	const { loading, data, error } = useQuery<
		WebPanelLocationQueryType,
		WebPanelLocationQueryVariablesType
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		WebPanelLocationQuery,
		{
			variables,
			fetchPolicy,
			onCompleted,
		},
	);

	const panels = (data?.webPanels || []).filter((panel) => panel) as WebPanel[];

	return {
		panels,
		loading,
		error,
	};
};
